// src/components/Footer.js
import React from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    FaPhone, FaEnvelope, FaMapMarkerAlt, FaFacebookF,
    FaTwitter, FaInstagram, FaLinkedinIn, FaWhatsapp,
    FaHeartbeat, FaBabyCarriage, FaHospitalUser,
    FaShieldAlt, FaStar
} from 'react-icons/fa';
import logo from '../media/logo.png';

const Footer = () => {
    const currentYear = new Date().getFullYear();

    // Luxury gradient colors
    const gradientColors = {
        primary: 'linear-gradient(135deg, #0d4550 0%, #1a6a7a 50%, #2a8d9c 100%)',
        secondary: 'linear-gradient(135deg, #1a6a7a 0%, #0d4550 100%)',
        gold: 'linear-gradient(135deg, #d4af37 0%, #f7ef8a 50%, #d4af37 100%)',
        coral: 'linear-gradient(135deg, #ff7f50 0%, #ff6347 100%)'
    };

    const socialIcons = [
        { icon: <FaFacebookF />, color: '#1877F2', label: 'Facebook' },
        { icon: <FaTwitter />, color: '#1DA1F2', label: 'Twitter' },
        { icon: <FaInstagram />, color: '#E4405F', label: 'Instagram' },
        { icon: <FaLinkedinIn />, color: '#0A66C2', label: 'LinkedIn' },
        { icon: <FaWhatsapp />, color: '#25D366', label: 'WhatsApp' }
    ];

    return (
        <footer style={{
            background: gradientColors.primary,
            color: 'white',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Decorative elements */}
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                background: gradientColors.gold
            }}></div>
            
            {/* Pattern overlay */}
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundImage: 'radial-gradient(circle at 25% 25%, rgba(255,255,255,0.05) 0%, transparent 55%), radial-gradient(circle at 75% 75%, rgba(255,255,255,0.05) 0%, transparent 55%)',
                pointerEvents: 'none'
            }}></div>

            <Container style={{ position: 'relative', zIndex: 1 }}>
                {/* Main Footer Content */}
                <Row className="py-5">
                    {/* Brand Column with Logo */}
                    <Col lg={3} md={6} className="mb-5 mb-lg-0">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                        >
                            <div className="d-flex align-items-center mb-4">
                                {/* Hospital Logo */}
                                <div style={{
                                    width: '60px',
                                    height: '60px',
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    borderRadius: '12px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    marginRight: '15px',
                                    border: '1px solid rgba(255, 255, 255, 0.2)',
                                    overflow: 'hidden',
                                    padding: '5px'
                                }}>
                                    {logo ? (
                                        <img 
                                            src={logo} 
                                            alt="Lotus Hospital Logo" 
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                                filter: 'brightness(1.2) drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                                            }}
                                            onError={(e) => {
                                                console.log('Footer logo image failed to load');
                                                e.target.style.display = 'none';
                                                e.target.parentElement.innerHTML = `
                                                    <div style="
                                                        width: 100%;
                                                        height: 100%;
                                                        background: linear-gradient(135deg, #2a8d9c 0%, #1a6a7a 100%);
                                                        border-radius: 10px;
                                                        display: flex;
                                                        align-items: center;
                                                        justify-content: center;
                                                    ">
                                                        <FaHeartbeat size={28} color="white" />
                                                    </div>
                                                `;
                                            }}
                                        />
                                    ) : (
                                        <div style={{
                                            width: '100%',
                                            height: '100%',
                                            background: 'linear-gradient(135deg, #2a8d9c 0%, #1a6a7a 100%)',
                                            borderRadius: '10px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <FaHeartbeat size={28} color="white" />
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <h3 style={{
                                        background: gradientColors.gold,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text',
                                        fontWeight: '800',
                                        fontSize: '1.8rem',
                                        margin: 0,
                                        lineHeight: 1.2
                                    }}>
                                        Lotus Hospital
                                    </h3>
                                    <p style={{
                                        margin: '5px 0 0 0',
                                        fontSize: '0.9rem',
                                        opacity: 0.9,
                                        fontWeight: '500',
                                        color: 'rgba(255, 255, 255, 0.9)'
                                    }}>
                                        Mother & Child Care
                                    </p>
                                </div>
                            </div>
                            <p style={{
                                opacity: 0.9,
                                lineHeight: 1.7,
                                marginBottom: '25px',
                                fontSize: '0.95rem'
                            }}>
                                Premier healthcare for women, mothers, and children. 
                                Delivering world-class medical excellence with compassion 
                                and cutting-edge technology.
                            </p>
                            
                            {/* Trust Badges */}
                            <div className="d-flex flex-wrap gap-3 mb-4">
                                <div style={{
                                    background: 'rgba(255, 255, 255, 0.08)',
                                    padding: '8px 15px',
                                    borderRadius: '20px',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    fontSize: '0.8rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <FaShieldAlt size={12} color="#2a8d9c" />
                                    ISO Certified
                                </div>
                                <div style={{
                                    background: 'rgba(255, 255, 255, 0.08)',
                                    padding: '8px 15px',
                                    borderRadius: '20px',
                                    border: '1px solid rgba(255, 255, 255, 0.1)',
                                    fontSize: '0.8rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px'
                                }}>
                                    <FaStar size={12} style={{ 
                                        background: gradientColors.gold,
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        backgroundClip: 'text'
                                    }} />
                                    JCI Accredited
                                </div>
                            </div>
                        </motion.div>
                    </Col>

                    {/* Quick Links */}
                    <Col lg={2} md={6} className="mb-5 mb-md-0">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                        >
                            <h5 style={{
                                color: 'white',
                                marginBottom: '25px',
                                fontSize: '1.1rem',
                                fontWeight: '600',
                                position: 'relative',
                                paddingBottom: '10px'
                            }}>
                                Quick Links
                                <div style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    width: '40px',
                                    height: '2px',
                                    background: gradientColors.gold
                                }}></div>
                            </h5>
                            <ul style={{
                                listStyle: 'none',
                                padding: 0,
                                margin: 0
                            }}>
                                {['Home', 'Services', 'Doctors', 'Facilities', 'Appointments', 'Contact'].map((item, index) => (
                                    <motion.li
                                        key={item}
                                        initial={{ opacity: 0, x: -10 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.3, delay: index * 0.05 }}
                                    >
                                        <Link
                                            to={`/${item.toLowerCase() === 'home' ? '' : item.toLowerCase()}`}
                                            style={{
                                                color: 'rgba(255, 255, 255, 0.85)',
                                                textDecoration: 'none',
                                                display: 'block',
                                                padding: '8px 0',
                                                transition: 'all 0.3s ease',
                                                fontSize: '0.95rem',
                                                borderLeft: '2px solid transparent',
                                                paddingLeft: '15px'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.target.style.color = 'white';
                                                e.target.style.borderLeftColor = gradientColors.gold;
                                                e.target.style.transform = 'translateX(5px)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.target.style.color = 'rgba(255, 255, 255, 0.85)';
                                                e.target.style.borderLeftColor = 'transparent';
                                                e.target.style.transform = 'translateX(0)';
                                            }}
                                        >
                                            {item}
                                        </Link>
                                    </motion.li>
                                ))}
                            </ul>
                        </motion.div>
                    </Col>

                    {/* Services */}
                    <Col lg={2} md={6} className="mb-5 mb-md-0">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            <h5 style={{
                                color: 'white',
                                marginBottom: '25px',
                                fontSize: '1.1rem',
                                fontWeight: '600',
                                position: 'relative',
                                paddingBottom: '10px'
                            }}>
                                Our Services
                                <div style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    width: '40px',
                                    height: '2px',
                                    background: gradientColors.gold
                                }}></div>
                            </h5>
                            <ul style={{
                                listStyle: 'none',
                                padding: 0,
                                margin: 0
                            }}>
                                {[
                                    { name: 'Maternity Care', icon: <FaBabyCarriage size={14} /> },
                                    { name: 'Neonatal ICU', icon: <FaHospitalUser size={14} /> },
                                    { name: 'Pediatrics', icon: <FaHeartbeat size={14} /> },
                                    { name: 'Gynecology', icon: <FaShieldAlt size={14} /> },
                                    { name: 'Fertility Center', icon: <FaStar size={14} /> }
                                ].map((service, index) => (
                                    <motion.li
                                        key={service.name}
                                        initial={{ opacity: 0, x: -10 }}
                                        whileInView={{ opacity: 1, x: 0 }}
                                        transition={{ duration: 0.3, delay: index * 0.05 }}
                                    >
                                        <div style={{
                                            color: 'rgba(255, 255, 255, 0.85)',
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '8px 0',
                                            fontSize: '0.95rem',
                                            gap: '10px'
                                        }}>
                                            <span style={{ color: gradientColors.gold }}>
                                                {service.icon}
                                            </span>
                                            {service.name}
                                        </div>
                                    </motion.li>
                                ))}
                            </ul>
                        </motion.div>
                    </Col>

                    {/* Contact & Emergency */}
                    <Col lg={4} md={6}>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.3 }}
                        >
                            <h5 style={{
                                color: 'white',
                                marginBottom: '25px',
                                fontSize: '1.1rem',
                                fontWeight: '600',
                                position: 'relative',
                                paddingBottom: '10px'
                            }}>
                                Contact Information
                                <div style={{
                                    position: 'absolute',
                                    bottom: 0,
                                    left: 0,
                                    width: '40px',
                                    height: '2px',
                                    background: gradientColors.gold
                                }}></div>
                            </h5>
                            
                            {/* Contact Details */}
                            <div style={{ marginBottom: '30px' }}>
                                <div className="d-flex align-items-start mb-4">
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        borderRadius: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: '15px',
                                        flexShrink: 0
                                    }}>
                                        <FaMapMarkerAlt size={16} color="#2a8d9c" />
                                    </div>
                                    <div>
                                        <h6 style={{ margin: '0 0 5px 0', fontSize: '0.95rem' }}>Our Location</h6>
                                        <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem' }}>
                                            123 Healthcare Street<br />
                                            Medical District, Dehradun 248001
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="d-flex align-items-center mb-4">
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        borderRadius: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: '15px',
                                        flexShrink: 0
                                    }}>
                                        <FaPhone size={16} color="#2a8d9c" />
                                    </div>
                                    <div>
                                        <h6 style={{ margin: '0 0 5px 0', fontSize: '0.95rem' }}>Phone Number</h6>
                                        <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem' }}>
                                            +91 135 265 4321
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="d-flex align-items-center mb-4">
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        borderRadius: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginRight: '15px',
                                        flexShrink: 0
                                    }}>
                                        <FaEnvelope size={16} color="#2a8d9c" />
                                    </div>
                                    <div>
                                        <h6 style={{ margin: '0 0 5px 0', fontSize: '0.95rem' }}>Email Address</h6>
                                        <p style={{ margin: 0, opacity: 0.9, fontSize: '0.9rem' }}>
                                            contact@lotushospital.com
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Emergency Box */}
                            <div style={{
                                background: 'rgba(255, 127, 80, 0.15)',
                                borderRadius: '15px',
                                padding: '20px',
                                border: '1px solid rgba(255, 127, 80, 0.3)',
                                position: 'relative',
                                overflow: 'hidden'
                            }}>
                                <div style={{
                                    position: 'absolute',
                                    top: '-50px',
                                    right: '-50px',
                                    width: '100px',
                                    height: '100px',
                                    background: gradientColors.coral,
                                    opacity: 0.1,
                                    borderRadius: '50%'
                                }}></div>
                                <h6 style={{
                                    color: '#ff7f50',
                                    marginBottom: '10px',
                                    fontSize: '1rem',
                                    fontWeight: '600'
                                }}>
                                    24/7 Emergency
                                </h6>
                                <p style={{
                                    fontSize: '1.4rem',
                                    fontWeight: '800',
                                    margin: '10px 0',
                                    color: 'white'
                                }}>
                                    +91 135 265 9999
                                </p>
                                <small style={{ opacity: 0.9, fontSize: '0.85rem' }}>
                                    Immediate medical assistance • Ambulance on call
                                </small>
                            </div>
                        </motion.div>
                    </Col>
                </Row>

                {/* Social Media & Bottom Bar */}
                <Row className="align-items-center py-4" style={{
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                    <Col md={6} className="mb-4 mb-md-0">
                        <div className="d-flex gap-3">
                            {socialIcons.map((social, index) => (
                                <motion.a
                                    key={social.label}
                                    href="#"
                                    whileHover={{ scale: 1.1, y: -3 }}
                                    whileTap={{ scale: 0.95 }}
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        background: 'rgba(255, 255, 255, 0.08)',
                                        borderRadius: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'white',
                                        textDecoration: 'none',
                                        transition: 'all 0.3s ease',
                                        border: '1px solid rgba(255, 255, 255, 0.1)'
                                    }}
                                    onMouseEnter={(e) => {
                                        e.target.style.background = social.color;
                                        e.target.style.borderColor = social.color;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                                        e.target.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                                    }}
                                >
                                    {social.icon}
                                </motion.a>
                            ))}
                        </div>
                    </Col>
                    
                    <Col md={6}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: '20px'
                        }}>
                            <Link to="/admin-login" style={{
                                color: 'rgba(255, 255, 255, 0.9)',
                                textDecoration: 'none',
                                fontSize: '0.9rem',
                                padding: '8px 20px',
                                borderRadius: '25px',
                                border: '1px solid rgba(255, 255, 255, 0.2)',
                                transition: 'all 0.3s ease',
                                background: 'rgba(255, 255, 255, 0.05)'
                            }}
                            onMouseEnter={(e) => {
                                e.target.style.background = gradientColors.gold;
                                e.target.style.color = '#0d4550';
                                e.target.style.borderColor = gradientColors.gold;
                            }}
                            onMouseLeave={(e) => {
                                e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                                e.target.style.color = 'rgba(255, 255, 255, 0.9)';
                                e.target.style.borderColor = 'rgba(255, 255, 255, 0.2)';
                            }}>
                                Admin Portal
                            </Link>
                            
                            <div style={{ display: 'flex', gap: '15px' }}>
                                <Link to="/privacy" style={{
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    textDecoration: 'none',
                                    fontSize: '0.85rem',
                                    transition: 'color 0.3s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.color = 'white'}>
                                    Privacy Policy
                                </Link>
                                <span style={{ color: 'rgba(255, 255, 255, 0.3)' }}>|</span>
                                <Link to="/terms" style={{
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    textDecoration: 'none',
                                    fontSize: '0.85rem',
                                    transition: 'color 0.3s ease'
                                }}
                                onMouseEnter={(e) => e.target.style.color = 'white'}>
                                    Terms of Service
                                </Link>
                            </div>
                        </div>
                    </Col>
                </Row>

                {/* Copyright */}
                <Row className="py-3">
                    <Col>
                        <div className="text-center" style={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontSize: '0.85rem'
                        }}>
                            <p style={{ margin: 0 }}>
                                © {currentYear} Lotus Hospital Dehradun. All rights reserved.
                            </p>
                            <p style={{ margin: '5px 0 0 0', fontSize: '0.8rem', opacity: 0.7 }}>
                                A unit of Lotus Healthcare Group • NABH Accredited • ISO 9001:2015 Certified
                            </p>
                        </div>
                    </Col>
                </Row>
            </Container>

            {/* Responsive CSS */}
            <style jsx="true">{`
                @media (max-width: 992px) {
                    .footer-col {
                        margin-bottom: 40px;
                    }
                    .social-icons {
                        justify-content: center;
                    }
                    .bottom-links {
                        justify-content: center !important;
                        margin-top: 20px;
                    }
                }
                
                @media (max-width: 768px) {
                    footer {
                        padding: 40px 0 20px !important;
                    }
                    .container {
                        padding-left: 20px;
                        padding-right: 20px;
                    }
                    h3 {
                        font-size: 1.6rem !important;
                    }
                    .trust-badges {
                        flex-direction: column;
                        align-items: flex-start;
                    }
                    .emergency-box {
                        margin-top: 20px;
                    }
                }
                
                @media (max-width: 576px) {
                    .brand-section {
                        text-align: center;
                    }
                    .brand-section .d-flex {
                        justify-content: center;
                        flex-direction: column;
                        text-align: center;
                    }
                    .brand-section .d-flex > div:first-child {
                        margin-right: 0;
                        margin-bottom: 15px;
                    }
                    .quick-links ul,
                    .services ul {
                        text-align: center;
                    }
                    .contact-info h6 {
                        font-size: 0.9rem;
                    }
                    .social-icons {
                        justify-content: center;
                    }
                    .admin-portal-btn {
                        width: 100%;
                        text-align: center;
                    }
                }
                
                @media (max-width: 375px) {
                    .emergency-box p {
                        font-size: 1.2rem !important;
                    }
                    .social-icons a {
                        width: 35px;
                        height: 35px;
                    }
                    .brand-section .d-flex > div:first-child {
                        width: 50px !important;
                        height: 50px !important;
                    }
                    h3 {
                        font-size: 1.4rem !important;
                    }
                }
                
                /* Smooth scrolling */
                html {
                    scroll-behavior: smooth;
                }
                
                /* Animation for links */
                @keyframes fadeInUp {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                
                .animate-fade-in-up {
                    animation: fadeInUp 0.6s ease-out;
                }
            `}</style>
        </footer>
    );
};

export default Footer;